import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import { Drawer } from '../../../../components/drawers';
import toast from '../../../../utils/toast';
import CompromisedEmployerFormFields from './CompromisedEmployerFormFields';

interface Employer {
  id: number;
  company_name: string;
}

interface AddCompromisedEmployerFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  compromisedEmployerIds: number[];
}

const AddCompromisedEmployerForm: React.FC<AddCompromisedEmployerFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  compromisedEmployerIds
}) => {
  const [selectedEmployerId, setSelectedEmployerId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [loadingEmployers, setLoadingEmployers] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchEmployers = async () => {
      setLoadingEmployers(true);
      try {
        const response = await axios.get<Employer[]>(`${environment.baseUrl}/employers/?status=active`);
        setEmployers(response.data);
      } catch {
        toast.error('Failed to load employers');
      } finally {
        setLoadingEmployers(false);
      }
    };

    fetchEmployers();
  }, [isOpen]);

  const handleClose = () => {
    setSelectedEmployerId(null);
    setReason('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployerId) return;

    setLoading(true);
    try {
      await axios.post(`${environment.baseUrl}/employer/update-compromised/`, {
        employer: selectedEmployerId,
        is_compromised: true,
        is_compromised_reason: reason
      });
      toast.success('Employer flagged as compromised');
      onSuccess();
      handleClose();
    } catch (err: any) {
      if (err.response?.status === 400) {
        toast.error(err.response?.data?.message || 'Invalid data provided');
      } else {
        toast.error(err.response?.data?.message || 'Failed to flag employer');
      }
    } finally {
      setLoading(false);
    }
  };

  const availableEmployers = employers.filter(
    (emp) => !compromisedEmployerIds.includes(emp.id)
  );

  const isValid = selectedEmployerId !== null;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header title="Flag Compromised Employer" onClose={handleClose} />

      <Drawer.Content>
        <form onSubmit={handleSubmit}>
          <CompromisedEmployerFormFields
            reason={reason}
            onReasonChange={setReason}
            employers={loadingEmployers ? [] : availableEmployers}
            selectedEmployerId={selectedEmployerId}
            onEmployerChange={setSelectedEmployerId}
            showAlert
          />
        </form>
      </Drawer.Content>

      <Drawer.Footer fullWidth>
        <Drawer.PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || loading || loadingEmployers}
        >
          {loading ? 'Saving...' : 'Save'}
        </Drawer.PrimaryButton>
      </Drawer.Footer>
    </Drawer>
  );
};

export default AddCompromisedEmployerForm;
